import { Component, inject, effect, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LucideAngularModule, Target, DollarSign, Info, CalendarDays } from 'lucide-angular';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { ReductionObjectivesService } from 'src/app/shared/services/reduction-objectives.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import {
  ObjectiveProgress,
  ReductionObjectiveType,
  UpsertObjectiveDto,
} from 'src/app/shared/model/reduction-objective';

interface ObjectiveCard {
  type: ReductionObjectiveType;
  labelKey: string;
  descriptionKey: string;
  unitKey: string;
  icon: typeof Target;
  isActive: boolean;
  targetValue: number | null;
  targetYear: number | null;
  baselineValue?: number | null;
}

@Component({
  selector: 'app-reduction-target',
  standalone: true,
  imports: [
    DecimalPipe,
    FormsModule,
    RouterLink,
    ButtonModule,
    DialogModule,
    InputNumberModule,
    ProgressBarModule,
    ToggleSwitchModule,
    TranslocoModule,
    LucideAngularModule,
  ],
  templateUrl: './reduction-target.component.html',
})
export class ReductionTargetComponent {
  readonly formatCo2eService = inject(FormatCo2eService);
  private currentUserService = inject(CurrentUserService);
  private objectivesService = inject(ReductionObjectivesService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);

  readonly targetIcon = Target;
  readonly dollarSignIcon = DollarSign;
  readonly infoIcon = Info;

  infoDialogVisible = signal(false);

  /**
   * "Where to learn more" link closing the "how to set your target" dialog. It points at the
   * Resources tab rather than a `?block=` deep link: documents, MOOCs and webinars all live
   * there, so the reader lands on every source the sentence mentions.
   */
  readonly helpResourcesRoute = '/home/help/resources';

  readonly currentYear = new Date().getFullYear();

  isLoading = signal(false);
  progress = signal<ObjectiveProgress[]>([]);
  companyId = '';

  cards: ObjectiveCard[] = [
    {
      type: 'AVG_EMISSION_PER_TRAVELLER',
      labelKey: 'objectives.avgEmissionPerTraveller',
      descriptionKey: 'objectives.avgEmissionPerTravellerDesc',
      unitKey: 'objectives.unitKgCo2e',
      icon: Target,
      isActive: false,
      targetValue: null,
      targetYear: null,
    },
    {
      type: 'AVG_EMISSION_PER_TRAVELLER_PER_DAY',
      labelKey: 'objectives.avgEmissionPerTravellerPerDay',
      descriptionKey: 'objectives.avgEmissionPerTravellerPerDayDesc',
      unitKey: 'objectives.unitKgCo2ePerDay',
      icon: CalendarDays,
      isActive: false,
      targetValue: null,
      targetYear: null,
    },
    {
      type: 'EMISSION_PER_TRIP_REVENUE',
      labelKey: 'objectives.emissionPerTripRevenue',
      descriptionKey: 'objectives.emissionPerTripRevenueDesc',
      unitKey: 'objectives.unitKgCo2ePerKeur',
      icon: DollarSign,
      isActive: false,
      targetValue: null,
      targetYear: null,
    },
    // TODO: EMISSION_REDUCTION_VS_YEAR — hidden until year-over-year calculation is implemented
  ];

  constructor() {
    effect(() => {
      const user = this.currentUserService.currentUser();
      if (user?.companyId) {
        this.companyId = user.companyId;
        this.loadProgress();
      }
      const objectives = this.currentUserService.objectives();
      for (const card of this.cards) {
        const obj = objectives.find((o) => o.type === card.type);
        if (obj) {
          card.isActive = obj.isActive;
          card.targetValue = obj.targetValue;
          card.targetYear = obj.targetYear ?? null;
          if ('baselineValue' in card) {
            card.baselineValue = obj.baselineValue ?? null;
          }
        } else {
          card.isActive = false;
          card.targetValue = null;
          card.targetYear = null;
          if ('baselineValue' in card) {
            card.baselineValue = null;
          }
        }
      }
    });
  }

  private loadProgress(): void {
    if (!this.companyId) return;
    this.objectivesService.getProgress(this.companyId).subscribe({
      next: (data) => this.progress.set(data),
    });
  }

  getProgressForCard(type: ReductionObjectiveType): ObjectiveProgress | null {
    return this.progress().find((p) => p.type === type) ?? null;
  }

  getDeltaChipClass(progressPercent: number): string {
    if (progressPercent >= 100) return 'border-green-200 bg-green-50 text-green-700';
    if (progressPercent >= 50) return 'border-orange-200 bg-orange-50 text-orange-700';
    return 'border-red-200 bg-red-50 text-red-700';
  }

  getDelta(prog: ObjectiveProgress): number {
    return prog.currentValue - prog.targetValue;
  }

  getDeltaPercent(prog: ObjectiveProgress): number {
    if (prog.currentValue === 0) return 0;
    return (this.getDelta(prog) / prog.currentValue) * 100;
  }

  co2eUnit(value: number, card: ObjectiveCard): string {
    let suffix = '';
    if (card.type === 'EMISSION_PER_TRIP_REVENUE' || card.type === 'EMISSION_PER_TRIP_PRICE') {
      suffix = '/k€';
    } else if (card.type === 'AVG_EMISSION_PER_TRAVELLER_PER_DAY') {
      suffix = '/day';
    }
    return this.formatCo2eService.getUnit(value) + suffix;
  }

  onSave(): void {
    if (!this.companyId) return;

    const objectivesToUpsert: UpsertObjectiveDto[] = this.cards
      .filter((card) => card.targetValue !== null && card.targetValue >= 0)
      .map((card) => ({
        type: card.type,
        targetValue: card.targetValue!,
        isActive: card.isActive,
        targetYear: card.targetYear ?? undefined,
        baselineValue: 'baselineValue' in card ? (card.baselineValue ?? undefined) : undefined,
      }));

    if (objectivesToUpsert.length === 0) return;

    this.isLoading.set(true);
    this.objectivesService.upsertObjectives(this.companyId, objectivesToUpsert).subscribe({
      next: () => {
        this.notificationService.success(
          this.translocoService.translate('objectives.saveSuccess')
        );
        this.isLoading.set(false);
        this.loadProgress();
      },
      error: () => {
        this.notificationService.error(
          this.translocoService.translate('objectives.saveError')
        );
        this.isLoading.set(false);
      },
    });
  }
}
